// ** MUI Imports
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// ** Type Import
import { Settings } from 'src/@core/context/settingsContext'

// ** Hook Import
import { Fragment, useEffect, useState } from 'react'
import NotificationDropdown from '@/src/@core/layouts/components/shared-components/NotificationDropdown'
import { useWebSocket } from '@/src/hooks/useWebSocket'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Button, Drawer, useMediaQuery } from '@mui/material'
import GygDrawer from '@/src/gygComponents/gygFaqDrawer'
import { useDispatch } from 'react-redux'
import { AppDispatch, RootState } from '@/src/store'
import { addNotification, Notification } from '@/src/store/apps/resto365Notifications'
import { useSelector } from 'react-redux'
import Link from 'next/link'
import { useAuth } from '@/src/hooks/useAuth'
import AvatarsLetter from '@/src/gygComponents/AvatarsLetter'

dayjs.extend(utc)

interface Props {
  hidden: boolean
  settings: Settings
  navVisible?: boolean
  toggleNavVisibility: () => void
  saveSettings: (values: Settings) => void
}

type NotificationCategory = 'menu' | 'restaurant'

function getCategoryLabel(category: NotificationCategory) {
  switch (category) {
    case 'menu':
      return 'Menu'
    case 'restaurant':
      return 'Restaurant'
    default:
      return ''
  }
}

const AppBarContent = (props: Props) => {
  const dispatch = useDispatch<AppDispatch>()
  const { logout, user } = useAuth()

  // ** Props
  const { hidden, settings, navVisible, saveSettings } = props
  const {
    notifications: { value: notifications }
  } = useSelector((state: RootState) => state.resto365Notifications)

  const [openDrawer, setOpenDrawer] = useState(false)
  const { socket, isConnected } = useWebSocket()

  // Removed unnecessary debug logging
  useEffect(() => {
    if (socket) {
      socket.on('notification', (notification: Notification) => {
        dispatch(addNotification({ ...notification, read: false }))
      })

      return () => {
        socket.off('notification')
      }
    }
  }, [socket, dispatch])

  const clickOnIcon = () => {
    saveSettings({
      ...settings,
      navCollapsed: false,
      isTabletMenuOpen: !settings.isTabletMenuOpen,
      isMobileMenuOpen: !settings.isMobileMenuOpen
    })
  }

  const toggleFAQDrawer = (newOpen: boolean) => () => {
    setOpenDrawer(newOpen)
  }

  const isMobile = useMediaQuery('(max-width:540px) and (orientation: portrait)')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box className='actions-left' sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {isMobile && settings.isMobileMenuOpen && settings.isTabletMenuOpen ? (
            <IconButton color='inherit' onClick={clickOnIcon}>
              <Icon icon='material-symbols:close' fontSize={'24px'} style={{ color: '#fff' }} />
            </IconButton>
          ) : (
            <Fragment>
              {(hidden && !settings.navHidden) || navVisible ? (
                <Fragment>
                  <IconButton color='inherit' onClick={clickOnIcon}>
                    <Icon fontSize='24px' icon='tabler:menu-2' style={{ color: 'white' }} />
                  </IconButton>

                  <svg xmlns='http://www.w3.org/2000/svg' width='46' height='31' viewBox='0 0 31 31' fill='none'>
                    <path
                      d='M17.0915 0H26.0179L26.5714 0.868534V1.00216H26.7098L26.9174 1.53664L26.9866 1.73707L27.1942 1.80388L27.9554 3.14009L28.3705 3.80819L28.7857 4.47629L29.6853 6.01293L30.2388 6.94828L30.308 7.28233L30.5156 7.34914L31 8.15086L30.9308 8.55172L30.1696 9.75431H30.0312L29.8929 10.0884L29.7545 10.4224L29.3393 11.1573L29.1317 11.4914H28.9933L28.9241 11.8922L28.5089 12.4935L28.0937 13.2284L27.0558 14.9655L26.779 15.3664L26.8482 15.7004L27.817 17.3039L28.3013 18.1056L28.7857 18.8405L29.3393 19.8427L29.4777 20.2435H29.6161L29.8929 20.778L29.9621 21.1121L30.1696 21.1789L31 22.5819L30.9308 22.9159L30.7924 23.1832L30.4464 23.7177H30.308L30.1696 24.1853L29.4085 25.4547H29.2701L29.2009 25.8556L28.9933 25.9224L28.9241 26.2565L28.3013 27.3254L27.3326 28.9289L27.0558 29.2629L26.9174 29.3297L26.8482 29.6638L26.433 30.4655L26.0179 31H17.0915L17.0223 30.7996L16.8147 30.7328L16.0536 29.4634L15.5 28.4612L14.8772 27.4591L14.3929 26.6573L14.0469 26.056L13.6317 25.3879L13.1473 24.653L12.7321 23.8513L4.98214 23.7845L4.56696 23.25L4.29018 22.7155L3.32143 21.1121L2.97545 20.5108L2.21429 19.3082L1.59152 18.306L1.45313 17.972L0.622768 16.6358L0 15.6336L0.0691964 15.2996L0.96875 13.7629L1.59152 12.7608L2.0067 12.0259L2.62946 10.9569L3.18304 10.0884L3.39062 9.75431L4.42857 8.01724L4.77455 7.34914H4.98214V7.21552L12.7321 7.14871L13.1473 6.41379L13.5625 5.67888L14.2545 4.5431L14.6004 4.00862L14.9464 3.40733L15.9152 1.80388L16.2612 1.20259L16.6071 0.601293L16.8839 0.200431H17.0223L17.0915 0ZM18.2679 2.20474L17.8527 2.80603L17.5067 3.47414L17.3683 3.60776L17.2991 3.80819L17.1607 3.94181L17.0223 4.27586L16.8839 4.40948L16.3304 5.41164L15.4308 6.88147L15.3616 7.14871L15.2232 7.21552L15.0848 7.61638L14.9464 7.68319L14.8772 7.95043L14.7388 8.01724L14.6696 8.21767L14.7388 8.55172L15.2232 9.35345L15.3616 9.42026V9.82112L15.5692 10.0884L15.846 10.3556L16.4688 11.4246L16.8839 12.0927V12.2263L17.0915 12.4267L17.1607 12.694L17.4375 12.9612L17.9219 13.8966L18.2679 14.3642H24.7723L25.1875 13.7629L25.8795 12.6272L26.0179 12.4935L26.1562 12.1595L26.3638 12.0259V11.8254H26.5022L26.5714 11.4914L27.3326 10.222V10.0884L27.5402 10.0216V9.75431L27.6786 9.6875L27.817 9.35345L27.9554 9.21983L28.0937 8.88578L28.3013 8.75215V8.61853L28.4397 8.21767L27.6094 6.88147L27.1942 6.07974L26.5714 5.07759L26.2946 4.5431L26.0871 4.20905L25.8103 3.80819V3.67457L25.6027 3.47414L25.5335 3.2069L25.2567 2.93966L24.8415 2.27155L24.7723 2.20474H18.2679ZM6.08929 9.42026L5.8817 9.88793L4.84375 11.625L4.29018 12.5603L4.08259 12.6272L3.9442 13.0948L3.18304 14.2974L3.04464 14.6983L2.90625 14.7651L2.76786 15.0991L2.62946 15.2328L2.56027 15.5668L2.97545 16.2349L3.39062 17.0366L3.66741 17.5043H3.8058L3.875 17.8384L4.01339 18.1724L4.15179 18.4397H4.29018L5.18973 19.9763L5.32813 20.3103L5.53571 20.5108L5.60491 20.778L5.8125 20.9784L5.7433 21.1121H5.95089L6.08929 21.4461L6.15848 21.5797H12.7321L13.2857 20.778L13.3549 20.5108L13.4933 20.3772L13.6317 20.0431L13.7701 19.9095L14.3237 18.9073L14.6004 18.5065L14.7388 18.3728L15.154 17.5711L15.9844 16.2349L16.3304 15.5668L16.192 15.1659L15.7076 14.2974L14.7388 12.694V12.5603L14.5312 12.3599V12.2263L14.3237 12.0259L14.2545 11.7586L13.9777 11.4914L13.2165 10.222L12.9397 9.6875L12.7321 9.42026H6.08929ZM18.3371 14.431V14.4978H24.7031V14.431H18.3371ZM18.2679 16.6358L17.8527 17.2371L17.7835 17.5043L17.6451 17.5711L17.5067 17.9052L17.3683 18.0388L17.2991 18.2392L17.1607 18.3728L16.7455 19.1078L15.7076 20.8448L15.5 20.9116L15.3616 21.2457V21.5797H15.2232L14.7388 22.4483L14.6696 22.5151V22.9159L14.7388 23.0496H14.8772L14.9464 23.3836H15.0848L15.2232 23.8513H15.3616L15.5 24.1853L16.192 25.3879L16.3996 25.7888L16.6763 26.1897V26.3233L16.8839 26.5237V26.6573L17.1607 26.9246L17.9219 28.3276L18.2679 28.7953H24.7723L25.0491 28.4612L25.4643 27.7263L26.1562 26.5905L26.2946 26.4569L26.7098 25.6552L27.5402 24.319L27.8862 23.6509L28.0246 23.5172L28.0937 23.3168L28.2321 23.1832L28.3705 22.8491L28.5089 22.7155L27.6094 21.2457L27.5402 20.9116L27.2634 20.6444L26.7098 19.709L25.9487 18.4397L25.7411 18.0388L25.5335 17.7047L25.1183 17.1034L24.8415 16.6358H18.2679ZM18.3371 28.8621V28.9289H24.7031V28.8621H18.3371Z'
                      fill='#FFD204'
                    />
                  </svg>
                </Fragment>
              ) : null}
            </Fragment>
          )}
        </Box>
        <Box className='actions-right' sx={{ display: 'flex', alignItems: 'center' }}>
          {(settings.isTabletMenuOpen || settings.isMobileMenuOpen) && (
            <Fragment>
              <Button onClick={() => logout()}>
                <Icon fontSize={24} color='#fff' icon='tabler:users' />
              </Button>
              <Button component={Link} href='/app/faq'>
                <Icon fontSize={24} color='#fff' icon='ic:outline-quiz' />
              </Button>
            </Fragment>
          )}
          <Button title='FAQ' onClick={toggleFAQDrawer(true)}>
            <Icon icon='ph:question' width='24px' height='24px' style={{ color: 'white' }} />
          </Button>
          <Drawer anchor='right' open={openDrawer}>
            <Box sx={{ position: 'absolute', top: '25px', right: '20px' }}>
              <Icon onClick={toggleFAQDrawer(false)} icon='material-symbols:close' />
            </Box>
            {<GygDrawer />}
          </Drawer>
          <NotificationDropdown
            settings={{
              skin: 'default',
              mode: 'light',
              appBar: undefined,
              footer: undefined,
              navHidden: undefined,
              appBarBlur: false,
              direction: 'ltr',
              navCollapsed: false,
              themeColor: 'primary',
              contentWidth: 'full',
              layout: undefined,
              lastLayout: undefined,
              verticalNavToggleType: 'accordion',
              toastPosition: undefined
            }}
            isSocketConnected={isConnected}
            notifications={notifications.map(notification => {
              const { message, category, createdAt, read } = notification

              return {
                meta: getCategoryLabel(category),
                title: message,
                subtitle: dayjs.utc(createdAt).fromNow(),
                read
              }
            })}
          />
          <Box sx={{ p: 2 }}>
            <AvatarsLetter name={user!.fullName} role={user!.role.name} />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default AppBarContent
